import { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_CREATE_ROUTE,
    COMPANY_ADMIN_ROUTE,
    getEditUserGroupRoute,
} from "../../../../../../constants/routes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    CompanyAdminUserGroupsQuery,
    UserGroupsForCompanyResponse,
    deleteUserGroupForCompany,
    fetchUserGroupsForCompany,
} from "../userGroupsApi"
import { UserGroup } from "../userGroupsTypes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

const selectUserGroups = (data: UserGroupsForCompanyResponse) => data?.userGroups || []

export function OrganizationUserGroups() {
    const { companyId } = useCurrentCompany()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const UserGroupsResult = useApiQuery(fetchUserGroupsForCompany, {
        queryName: CompanyAdminUserGroupsQuery.LIST,
        dependencies: {
            companyId,
        },
        select: selectUserGroups,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(UserGroupsResult)

    const columns = useMemo<MRT_ColumnDef<UserGroup>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-user-groups-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-user-groups-table-domain-column"),
                accessorKey: "domains",
                Cell: ({ row }) => {
                    return (
                        <Grid container flexDirection="column">
                            {row.original?.domains?.map((el) => <span key={el}>{el}</span>)}
                        </Grid>
                    )
                },
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-organization-user-groups-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteUserGroup } = useApiMutation(deleteUserGroupForCompany, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminUserGroupsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteUserGroup({ userGroupId: confirmModalState.itemId })
        } else {
            throw new Error("User group id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteUserGroup])

    return (
        <>
            <LocalizedStrict id="company-organization-user-groups-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_USER_GROUPS}
                    title="User groups"
                    rightElement={
                        <LocalizedStrict id="company-organization-user-groups-create-button">
                            <StandardButton
                                onClick={() => navigate(COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_CREATE_ROUTE)}
                            >
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-user-groups-title">
                            <Breadcrumb>User groups</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="company-organization-user-groups-table-edit">
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditUserGroupRoute(row.original.id))
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                Edit
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="company-organization-user-groups-table-delete">
                            <DestructiveMenuItem
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.name}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                Delete
                            </DestructiveMenuItem>
                        </LocalizedStrict>,
                    ]}
                />
            </Grid>

            {confirmModalState.showConfirmModal && (
                <LocalizedStrict
                    id="company-organization-user-groups-table-delete-modal-title"
                    attrs={{ typeOfEntity: true }}
                >
                    <DeleteConfirmDialog
                        typeOfEntity="user group"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
